import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Save, Eye, EyeOff, GripVertical, Image } from 'lucide-react';

const AdminBeforeAfter = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  useEffect(() => {
    loadItems();
  }, []);

  const loadItems = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch('/api/admin/before-after/items', {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      setItems(data.items || []);
    } catch (error) {
      console.error('Error loading items:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveItem = async (item) => {
    setSaving(true);
    try {
      const token = localStorage.getItem('admin_token');
      const isNew = !item.id || item.id.startsWith('new-');
      
      const url = isNew 
        ? '/api/admin/before-after/items' 
        : `/api/admin/before-after/items/${item.id}`;
      
      const response = await fetch(url, {
        method: isNew ? 'POST' : 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(item)
      });

      if (response.ok) {
        await loadItems();
        setEditingItem(null);
      }
    } catch (error) {
      console.error('Error saving item:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteItem = async (itemId) => {
    if (!window.confirm('Удалить этот элемент?')) return;
    
    try {
      const token = localStorage.getItem('admin_token');
      await fetch(`/api/admin/before-after/items/${itemId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      await loadItems();
    } catch (error) {
      console.error('Error deleting item:', error);
    }
  };

  const handleAddNew = () => {
    const newItem = {
      id: `new-${Date.now()}`,
      before_image: '',
      after_image: '',
      title_de: '',
      title_ru: '',
      description_de: '',
      description_ru: '',
      active: true,
      sort_order: items.length
    };
    setEditingItem(newItem);
  };

  const handleToggleActive = async (item) => {
    const updatedItem = { ...item, active: !item.active };
    await handleSaveItem(updatedItem);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-semibold text-white">До / После</h1>
          <p className="text-gray-400 mt-1">
            Управление слайдами сравнения на главной странице
          </p>
        </div>
        <button
          onClick={handleAddNew}
          className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
        >
          <Plus className="h-5 w-5" />
          Добавить
        </button>
      </div>

      {/* Item List */}
      <div className="space-y-4">
        {items.map((item, index) => (
          <div
            key={item.id}
            className="bg-gray-800 rounded-lg p-4 border border-gray-700"
          >
            <div className="flex items-start gap-4">
              {/* Preview Images */}
              <div className="flex gap-2 flex-shrink-0">
                <div className="relative">
                  <img
                    src={item.before_image}
                    alt="Before"
                    className="w-24 h-16 object-cover rounded"
                    onError={(e) => e.target.src = '/api/placeholder/96/64'}
                  />
                  <span className="absolute bottom-0 left-0 text-xs bg-black/70 text-white px-1">До</span>
                </div>
                <div className="relative">
                  <img
                    src={item.after_image}
                    alt="After"
                    className="w-24 h-16 object-cover rounded"
                    onError={(e) => e.target.src = '/api/placeholder/96/64'}
                  />
                  <span className="absolute bottom-0 right-0 text-xs bg-white/90 text-black px-1">После</span>
                </div>
              </div>

              {/* Content */}
              <div className="flex-1">
                <h3 className="text-white font-medium">
                  {item.title_de || 'Без названия'}
                </h3>
                <p className="text-gray-400 text-sm line-clamp-1">
                  {item.description_de || 'Нет описания'}
                </p>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handleToggleActive(item)}
                  className={`p-2 rounded-lg transition-colors ${
                    item.active 
                      ? 'text-green-400 hover:bg-green-400/10' 
                      : 'text-gray-500 hover:bg-gray-700'
                  }`}
                  title={item.active ? 'Активен' : 'Неактивен'}
                >
                  {item.active ? <Eye className="h-5 w-5" /> : <EyeOff className="h-5 w-5" />}
                </button>
                <button
                  onClick={() => setEditingItem(item)}
                  className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition-colors"
                  title="Редактировать"
                >
                  <Image className="h-5 w-5" />
                </button>
                <button
                  onClick={() => handleDeleteItem(item.id)}
                  className="p-2 text-red-400 hover:text-red-300 hover:bg-red-400/10 rounded-lg transition-colors"
                  title="Удалить"
                >
                  <Trash2 className="h-5 w-5" />
                </button>
              </div>
            </div>
          </div>
        ))}

        {items.length === 0 && (
          <div className="text-center py-12 text-gray-400">
            <Image className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>Нет элементов сравнения</p>
            <button
              onClick={handleAddNew}
              className="mt-4 text-indigo-400 hover:text-indigo-300"
            >
              Добавить первый элемент
            </button>
          </div>
        )}
      </div>

      {/* Edit Modal */}
      {editingItem && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 space-y-6">
              <h2 className="text-xl font-semibold text-white">
                {editingItem.id?.startsWith('new-') ? 'Новый элемент' : 'Редактирование'}
              </h2>

              {/* Images */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Изображение "До" (URL)
                  </label>
                  <input
                    type="url"
                    value={editingItem.before_image}
                    onChange={(e) => setEditingItem({ ...editingItem, before_image: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-indigo-500"
                    placeholder="https://..."
                  />
                  {editingItem.before_image && (
                    <img
                      src={editingItem.before_image}
                      alt="Preview"
                      className="mt-2 w-full h-32 object-cover rounded"
                      onError={(e) => e.target.style.display = 'none'}
                    />
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Изображение "После" (URL)
                  </label>
                  <input
                    type="url"
                    value={editingItem.after_image}
                    onChange={(e) => setEditingItem({ ...editingItem, after_image: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-indigo-500"
                    placeholder="https://..."
                  />
                  {editingItem.after_image && (
                    <img
                      src={editingItem.after_image}
                      alt="Preview"
                      className="mt-2 w-full h-32 object-cover rounded"
                      onError={(e) => e.target.style.display = 'none'}
                    />
                  )}
                </div>
              </div>

              {/* German Content */}
              <div className="space-y-4 border-t border-gray-700 pt-4">
                <h3 className="text-white font-medium">🇩🇪 Немецкий</h3>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Заголовок (DE)
                  </label>
                  <input
                    type="text"
                    value={editingItem.title_de}
                    onChange={(e) => setEditingItem({ ...editingItem, title_de: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-indigo-500"
                    placeholder="z.B. Wohnzimmer Staging"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Описание (DE)
                  </label>
                  <textarea
                    value={editingItem.description_de}
                    onChange={(e) => setEditingItem({ ...editingItem, description_de: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-indigo-500"
                    rows={2}
                    placeholder="Beschreibung auf Deutsch"
                  />
                </div>
              </div>

              {/* Russian Content */}
              <div className="space-y-4 border-t border-gray-700 pt-4">
                <h3 className="text-white font-medium">🇷🇺 Русский</h3>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Заголовок (RU)
                  </label>
                  <input
                    type="text"
                    value={editingItem.title_ru}
                    onChange={(e) => setEditingItem({ ...editingItem, title_ru: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-indigo-500"
                    placeholder="Напр. Стейджинг гостиной"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Описание (RU)
                  </label>
                  <textarea
                    value={editingItem.description_ru}
                    onChange={(e) => setEditingItem({ ...editingItem, description_ru: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-indigo-500"
                    rows={2}
                    placeholder="Описание на русском"
                  />
                </div>
              </div>

              {/* Sort Order */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Порядок сортировки
                </label>
                <input
                  type="number"
                  value={editingItem.sort_order}
                  onChange={(e) => setEditingItem({ ...editingItem, sort_order: parseInt(e.target.value) || 0 })}
                  className="w-24 px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-indigo-500"
                />
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4 border-t border-gray-700">
                <button
                  onClick={() => setEditingItem(null)}
                  className="px-4 py-2 text-gray-400 hover:text-white transition-colors"
                >
                  Отмена
                </button>
                <button
                  onClick={() => handleSaveItem(editingItem)}
                  disabled={saving}
                  className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50"
                >
                  <Save className="h-5 w-5" />
                  {saving ? 'Сохранение...' : 'Сохранить'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminBeforeAfter;
